/**
 * Internal Job-Status API Contract
 *
 * GET /internal/platform-data-sync/jobs/:jobPublicId
 *
 * API-key-gated (`INTERNAL_API_KEY`) read of a single sync job's polling
 * subset, used by the platform-sync operations console to poll a triggered job
 * to a terminal state without a Clerk session and without touching the DB
 * directly.
 *
 * Security posture (see the sync-operations-console requirements, Gap 1):
 *   - `metadata` is NEVER returned (sync executors write per-user identifiers
 *     there) — there is no `metadata` field in this response.
 *   - `error` is sanitized server-side (URLs/tokens stripped, truncated).
 *   - Optional `school_id` query param scopes the read: a job whose school does
 *     not match returns 404 (same as an unknown id — existence is not leaked).
 */

import { z } from 'zod'
import { SyncJobStatusSchema } from '../../schemas/entities/sync'

/**
 * Request Schema
 *
 * Path param `jobPublicId`; optional `school_id` query (public-id or slug).
 */
export const GetInternalJobStatusRequestSchema = z.object({
  jobPublicId: z.string().describe('Sync job public ID (CUID)'),
  school_id: z.string().optional().describe('Optional school public ID or slug; 404 if the job is not for this school')
})

export type GetInternalJobStatusRequest = z.infer<typeof GetInternalJobStatusRequestSchema>

/**
 * The polling-relevant subset of a sync job. NO `metadata` key by design.
 * `error` is the sanitized projection, never the raw stored value.
 */
export const InternalJobStatusSchema = z.object({
  id: z.string().describe('Sync job public ID'),
  schoolId: z.string().nullable().describe("Owning school's public ID"),
  syncType: z.string(),
  status: SyncJobStatusSchema,
  // All five lifecycle timestamps (ISO strings over the wire). `lastHeartbeatAt`
  // lets the poll loop distinguish a freshly-queued PENDING from a stuck one.
  queuedAt: z.string().nullable(),
  startedAt: z.string().nullable(),
  claimedAt: z.string().nullable(),
  lastHeartbeatAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  // The five stat counters.
  totalItemsProcessed: z.number(),
  totalNewItems: z.number(),
  totalUpdatedItems: z.number(),
  totalFailedItems: z.number(),
  totalErrorItems: z.number(),
  // Sanitized error (URLs/tokens stripped, truncated). Never the raw value.
  error: z.string().nullable()
})

export type InternalJobStatus = z.infer<typeof InternalJobStatusSchema>

/**
 * Response Schema
 */
export const GetInternalJobStatusResponseSchema = z.object({
  message: z.string(),
  data: InternalJobStatusSchema
})

export type GetInternalJobStatusResponse = z.infer<typeof GetInternalJobStatusResponseSchema>
